// Pure helpers for the /early-access form (ocl_online#170). No imports, so
// the unit tests run in bare Node: constants.js pulls in package.json, which
// Node's ESM loader refuses without an import attribute. Callers pass the
// contact address in instead.

export const EARLY_ACCESS_PATH = '/early-access';

export const DIRECT_SOURCE = 'direct';
const SOURCE_RE = /^[a-z0-9-]{1,40}$/;

// Brevo's single-line form fields hold 200 characters and its multi-line one
// 500, but the site caps the message at 400 so Brevo's line-break expansion
// (see messageLength below) never pushes it over (D14). OCL username is
// optional (D13) but still capped like the other single-line fields.
export const MAX_LENGTHS = { firstName: 200, lastName: 200, organization: 200, username: 200, message: 400 };

// Multipart FormData sends each line break as CRLF, so it counts as 2 toward
// the message cap (D14).
export const messageLength = (text) => text.length + (text.match(/\n/g) || []).length;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EMPTY_VALUES = {
  email: '', firstName: '', lastName: '', organization: '', username: '', message: '',
};

// The `?source=` slug is the only structured context a link carries.
export const earlyAccessPath = (source) => {
  const params = new URLSearchParams();
  if (source)
    params.set('source', source);
  const query = params.toString();
  return query ? `${EARLY_ACCESS_PATH}?${query}` : EARLY_ACCESS_PATH;
};

export const readParams = (searchParams) => {
  const source = searchParams.get('source');
  return {
    source: source && SOURCE_RE.test(source) ? source : DIRECT_SOURCE,
  };
};

// OCL profiles carry first_name/last_name, but older accounts may only have
// `name`; split that at its first space rather than leave both fields blank.
export const prefillFromUser = (user) => {
  if (!user)
    return {};
  let firstName = (user.first_name || '').trim();
  let lastName = (user.last_name || '').trim();
  if (!firstName && !lastName && user.name) {
    const name = user.name.trim();
    const space = name.indexOf(' ');
    firstName = space === -1 ? name : name.slice(0, space);
    lastName = space === -1 ? '' : name.slice(space + 1).trim();
  }
  return {
    email: (user.email || '').trim(),
    firstName,
    lastName,
    organization: (user.company || '').trim(),
    username: user.username || '',
  };
};

// Returns { field: errorCode } for every invalid field, empty when valid.
// Codes map to early_access.errors.<code> in the translations. OCL username
// is the one visible field that isn't required (D13): only its length is
// checked, and only when the visitor typed something.
export const validate = (values) => {
  const errors = {};
  const required = (field) => {
    if (!values[field] || !values[field].trim())
      errors[field] = 'required';
  };
  // Measures only once `required` has passed, so a missing value never reaches .trim().
  const maxLength = (field, measure = text => text.length) => {
    if (!errors[field] && measure(values[field].trim()) > MAX_LENGTHS[field])
      errors[field] = 'too_long';
  };

  required('email');
  if (!errors.email && !EMAIL_RE.test(values.email.trim()))
    errors.email = 'email';
  ['firstName', 'lastName', 'organization'].forEach(field => {
    required(field);
    maxLength(field);
  });
  required('message');
  // Weighted, not the raw trimmed length: line breaks count as 2 (D14).
  maxLength('message', messageLength);
  if ((values.username || '').trim().length > MAX_LENGTHS.username)
    errors.username = 'too_long';
  return errors;
};

// The exact field names the Brevo form "OCL early access request" expects
// (docs/superpowers/specs/2026-09-24-early-access-form-design.md, "Brevo
// build"). COMPANY and USERNAME are existing Brevo attributes, shared with
// the contacts imported from OCL's "90d Active Users" list. USERNAME comes
// from the visible, editable username field (D13) and is left out when
// blank, so a visitor who clears it can't erase a stored username. EA_SOURCE
// and EA_MESSAGE go on every request so together they always describe the
// latest one.
export const toBrevoFields = (values, { source, captchaToken }) => {
  const fields = {
    EMAIL: values.email.trim(),
    FIRSTNAME: values.firstName.trim(),
    LASTNAME: values.lastName.trim(),
    COMPANY: values.organization.trim(),
    EA_SOURCE: source,
    EA_MESSAGE: values.message.trim(),
    locale: 'en',
    email_address_check: '',
    'g-recaptcha-response': captchaToken,
  };
  const username = (values.username || '').trim();
  if (username)
    fields.USERNAME = username;
  return fields;
};

// A mailto: carrying everything the visitor typed, for when Brevo or
// reCAPTCHA fails — nothing they wrote is lost.
export const mailtoFallback = (values, to) => {
  const body = [
    `Name: ${values.firstName.trim()} ${values.lastName.trim()}`.trim(),
    `Email: ${values.email.trim()}`,
    `Organization: ${values.organization.trim()}`,
    `OCL username: ${(values.username || '').trim()}`,
    '',
    values.message.trim(),
  ].join('\n');
  return `mailto:${to}?subject=${encodeURIComponent('OCL early access')}&body=${encodeURIComponent(body)}`;
};
